import axios from 'axios';
import {AxiosResponse} from 'axios';
import store from '@/store';
import {ApiBase} from '@/api/common';

export class ApiListResponse<T> {
    public count?: number;
    public next?: string|null;
    public previous?: string|null;
    public results?: T[]|null;
    public length: number = 0;
}

export default class ApiList<T extends ApiBase<T>> extends ApiListResponse<T> {
    private readonly create: (info: any) => T;
    
    constructor(source: ApiListResponse<ApiBase<T>>, create: (info: any) => T) {
        super();
        this.create = create;
        this.count = source.count;
        this.next = source.next;
        this.previous = source.previous;
        if (source.results) {
            // API responses don't actually contain T objects, but
            // we can get real T objects from the create function.
            this.results = source.results.map(create);
            this.length = this.results.length;
        } else {
            this.results = null;
        }
    }
    
    /**
     * Reads the remaining items into this ApiList.
     *
     * @returns {Promise<void>}
     */
    public async readRemaining(): Promise<void> {
        if (!this.results) {
            this.results = [];
        }
        
        let list: ApiList<T> = this;
        while (true) {
            const next = await list.getNext();
            if (!next || !next.results) {
                break;
            }
            
            this.results.push(...next.results);
            list = next;
        }
    }
    
    public async getNext(): Promise<ApiList<T>|null> {
        if (!this.next) {
            return null;
        } else {
           return this.get(this.next);
        }
    }
    
    public getIndex(index: number): T|null {
        if (this.results) {
            return this.results[index];
        }
        return null;
    }
    
    public async getPrevious(): Promise<ApiList<T>|null> {
        if (!this.previous) {
            return null;
        } else {
            return this.get(this.previous);
        }
    }
    
    private async get(url: string): Promise<ApiList<T>> {
        const response: AxiosResponse<ApiListResponse<T>> = await axios({
            method: 'GET',
            url,
            headers: {
                'X-Session': store.state.sessionToken,
            },
        });
        
        return new ApiList<T>(response.data, this.create);
    }
    
    public async filterById(id: number) {
        if (this.results) {
            this.results = this.results.filter((e) => e.id !== id);
        }
    }
    
    public async push(element: T) {
        if (!this.results) {
            this.results = [];
        }
        
        if (this.results) {
            this.results.push(element);
        }
    }
    
    public async update(element: T) {
        if (!this.results) {
            return;
        }
    
        const index = this.results.findIndex((i) => i.id === element.id);
        
        if (index !== -1) {
            this.results[index].assign(element);
        }
    }
}
